import { useState, useEffect, useRef, useMemo } from "react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { ScrollArea } from "./ui/scroll-area";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import {
  MessageCircle, X, Send, Loader2, Bot, User, Minimize2, Maximize2, Trash2,
  Expand, Shrink, Sparkles, ArrowRight, Wrench, ChevronDown, ChevronRight,
  CheckCircle2, AlertTriangle, AlertCircle, History, Plus, MessageSquare, ArrowLeft,
} from "lucide-react";
import { cn } from "../lib/utils";
import { motion, AnimatePresence, useMotionValue } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import ChatChart from "./ChatChart";
import logger from "../lib/logger";
import { textoDeErrorDelChat, textoDeTarjetaDelChat } from "../lib/guardasClasificar";

// Markdown link renderer: React-Router navigation for internal URLs ("/...") and
// new-tab anchor for everything else.
function ChatLink({ href, children, ...rest }) {
  const url = href || "";
  const isInternal = url.startsWith("/");
  if (isInternal) {
    return (
      <RouterLink to={url} className="text-primary underline hover:opacity-80" {...rest}>
        {children}
      </RouterLink>
    );
  }
  // Only real absolute URLs render as anchors. Anything else (e.g. a placeholder
  // href hallucinated by the model like "link_to_transactions_x") degrades to
  // plain text instead of a broken link.
  if (!/^https?:\/\//i.test(url)) {
    return <span>{children}</span>;
  }
  return (
    <a className="text-primary underline" target="_blank" rel="noopener noreferrer" href={url} {...rest}>
      {children}
    </a>
  );
}

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Build fetch init options that include the session cookie and, if present,
 * the tab-scoped impersonation token.
 *
 * Regular session lives in an httpOnly cookie that the browser sends
 * automatically when `credentials: "include"` is set. Impersonation overrides
 * the identity per tab via sessionStorage + Authorization header (same scheme
 * as the axios interceptor in AuthContext).
 */
function authFetchInit(extra = {}) {
  const headers = { ...(extra.headers || {}) };
  const impersonationToken = sessionStorage.getItem("impersonation_token");
  if (impersonationToken) {
    headers.Authorization = `Bearer ${impersonationToken}`;
  }
  return { credentials: "include", ...extra, headers };
}

// Hide the <followups>...</followups> JSON marker from the live streaming bubble.
// While streaming we may receive a partial tag like "<followu" — also strip those
// so the user never sees raw markup before the "done" event arrives.
function sanitizeStreaming(text) {
  if (!text) return text;
  let out = text;
  // Strip a complete <followups>...</followups> block.
  out = out.replace(/<followups>[\s\S]*?<\/followups>/gi, "");
  // If we have a dangling <followups> opening, hide everything from it onward.
  const open = out.toLowerCase().lastIndexOf("<followups>");
  if (open !== -1) out = out.slice(0, open);
  // Handle orphan </followups> + nearby JSON array (Gemini sometimes emits closing tag without opening).
  out = out.replace(/<\/followups>/gi, "");
  // Drop any trailing JSON array of strings (the followups payload that may have leaked without tags).
  out = out.replace(/\s*\[\s*(?:"[^"]*"\s*(?:,\s*"[^"]*"\s*)*)?\]\s*$/, "");
  // Hide partial trailing "<f", "<fo", ..., "<followups" so it doesn't flicker while streaming.
  out = out.replace(/<\/?f(?:o(?:l(?:l(?:o(?:w(?:u(?:p(?:s>?)?)?)?)?)?)?)?)?$/i, "");
  return out.trimEnd();
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1 px-1 py-2">
      {[0, 1, 2].map(i => (
        <span
          key={i}
          className="w-1.5 h-1.5 rounded-full bg-primary/60"
          style={{
            animation: "chatbot-bounce 1.2s ease-in-out infinite",
            animationDelay: `${i * 0.15}s`,
          }}
        />
      ))}
      <style>{`
        @keyframes chatbot-bounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
          30% { transform: translateY(-4px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// Tarjeta de confirmación para acciones de escritura propuestas por el asistente.
// El backend solo ejecuta la acción cuando el usuario pulsa Confirmar.
function ActionProposalCard({ proposal, onConfirm, onReject, t }) {
  const busy = proposal.status === "confirming" || proposal.status === "rejecting";
  const finished = !["pending", "confirming", "rejecting"].includes(proposal.status);
  return (
    <div className="mt-2 rounded-md border border-primary/30 bg-primary/5 p-2.5">
      <div className="flex items-center gap-1.5 mb-1 text-primary">
        <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
        <p className="text-xs font-medium">{t("chatbot.actions.title")}</p>
      </div>
      <p className="text-sm mb-2 leading-snug">{proposal.summary}</p>
      {!finished ? (
        <div className="flex gap-2">
          <Button size="sm" className="h-7 px-3 text-xs" disabled={busy} onClick={onConfirm}>
            {proposal.status === "confirming"
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : t("chatbot.actions.confirm")}
          </Button>
          <Button size="sm" variant="outline" className="h-7 px-3 text-xs" disabled={busy} onClick={onReject}>
            {t("chatbot.actions.cancel")}
          </Button>
        </div>
      ) : (
        <p className={cn(
          "text-xs font-medium",
          proposal.status === "executed" ? "text-green-600 dark:text-green-400" : "text-muted-foreground"
        )}>
          {proposal.status === "executed" && (proposal.resultMsg || t("chatbot.actions.confirmed"))}
          {proposal.status === "rejected" && t("chatbot.actions.rejected")}
          {proposal.status === "expired" && t("chatbot.actions.expired")}
          {proposal.status === "failed" && (proposal.resultMsg || t("chatbot.actions.error"))}
        </p>
      )}
    </div>
  );
}

const SHOW_WORK_KEY = "trueflow_chatbot_show_work";

// Group sessions by recency relative to "now". Returns an ordered list of
// { key, label, items } so the panel renders Today / Yesterday / 7d / 30d / Older.
function groupSessionsByRecency(sessions, t) {
  if (!Array.isArray(sessions) || sessions.length === 0) return [];
  const now = Date.now();
  const day = 24 * 60 * 60 * 1000;
  const buckets = [
    { key: "today", label: t("chatbot.history.today"), items: [] },
    { key: "yesterday", label: t("chatbot.history.yesterday"), items: [] },
    { key: "last7", label: t("chatbot.history.last7"), items: [] },
    { key: "last30", label: t("chatbot.history.last30"), items: [] },
    { key: "older", label: t("chatbot.history.older"), items: [] },
  ];
  for (const s of sessions) {
    const ts = s?.last_message ? new Date(s.last_message).getTime() : 0;
    const ageDays = (now - ts) / day;
    if (ageDays < 1) buckets[0].items.push(s);
    else if (ageDays < 2) buckets[1].items.push(s);
    else if (ageDays < 7) buckets[2].items.push(s);
    else if (ageDays < 30) buckets[3].items.push(s);
    else buckets[4].items.push(s);
  }
  return buckets.filter(b => b.items.length > 0);
}

function HistoryPanel({ sessions, currentSessionId, onSelect, onDelete, onNew, onClose, t }) {
  const groups = groupSessionsByRecency(sessions, t);
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between p-3 border-b bg-background">
        <button
          type="button"
          onClick={onClose}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("chatbot.history.back")}
        </button>
        <Button size="sm" onClick={onNew} className="h-7 px-2">
          <Plus className="w-3.5 h-3.5 mr-1" />
          {t("chatbot.history.newConversation")}
        </Button>
      </div>
      <ScrollArea className="flex-1">
        {groups.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            {t("chatbot.history.empty")}
          </div>
        ) : (
          <div className="p-2 space-y-3">
            {groups.map(group => (
              <div key={group.key}>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground px-2 mb-1">
                  {group.label}
                </p>
                <div className="space-y-0.5">
                  {group.items.map(s => {
                    const isActive = s.session_id === currentSessionId;
                    const title = s.title || t("chatbot.history.untitled");
                    return (
                      <div
                        key={s.session_id}
                        className={cn(
                          "group flex items-start gap-2 px-2 py-1.5 rounded cursor-pointer text-sm",
                          isActive ? "bg-primary/10" : "hover:bg-muted"
                        )}
                        onClick={() => onSelect(s.session_id)}
                      >
                        <MessageSquare className="w-3.5 h-3.5 mt-0.5 text-muted-foreground shrink-0" />
                        <span className="flex-1 truncate">{title}</span>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onDelete(s.session_id); }}
                          className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
                          title={t("chatbot.history.delete")}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

function ToolCallsDetails({ toolCalls, t }) {
  const [open, setOpen] = useState(false);
  if (!toolCalls || toolCalls.length === 0) return null;

  return (
    <div className="mt-2 -mx-1">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors px-1"
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <Wrench className="w-3 h-3" />
        <span>{t("chatbot.toolDetails")} ({toolCalls.length})</span>
      </button>
      {open && (
        <div className="mt-1.5 space-y-1.5">
          {toolCalls.map((tc, i) => {
            const hasError = tc.status === "error" || tc.error;
            const toolWarn = tc.tool_returned_error;
            const Icon = hasError ? AlertCircle : toolWarn ? AlertTriangle : CheckCircle2;
            const iconClass = hasError
              ? "text-destructive"
              : toolWarn
                ? "text-amber-500"
                : "text-emerald-500";
            const argsStr = tc.args && Object.keys(tc.args).length
              ? JSON.stringify(tc.args, null, 0)
              : "—";
            return (
              <div
                key={i}
                className="rounded border border-border bg-background/40 px-2 py-1.5 text-[11px] font-mono"
              >
                <div className="flex items-center gap-1.5">
                  <Icon className={cn("w-3 h-3 shrink-0", iconClass)} />
                  <span className="font-semibold">{tc.name}</span>
                  <span className="ml-auto text-muted-foreground">{tc.latency_ms}ms</span>
                </div>
                <div className="mt-1 text-muted-foreground break-all">
                  {argsStr}
                </div>
                {tc.error && (
                  <div className="mt-1 text-destructive break-all">{tc.error}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const MARKDOWN_COMPONENTS = {
  p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
  ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-2 space-y-0.5" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-2 space-y-0.5" {...props} />,
  li: ({ node, ...props }) => <li {...props} />,
  strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
  pre: ({ node, children, ...props }) => {
    // Skip the <pre> wrapper when its only child is a chart fence — react-markdown
    // wraps every fenced block in <pre><code>, but our chart renderer returns a div.
    const child = Array.isArray(children) ? children[0] : children;
    const childClass = child?.props?.className;
    if (typeof childClass === "string" && childClass.includes("language-chart")) {
      return child;
    }
    // El bloque se dibuja ACÁ, no en `code`. `pre` es el único elemento que
    // react-markdown reserva para el código en bloque, así que es el único lugar
    // donde distinguirlo del código en línea no depende de adivinar. Las variantes
    // `[&>code]` apagan la píldora del código en línea, que adentro del bloque
    // duplicaría fondo y padding.
    return (
      <pre
        className="block bg-black/5 dark:bg-white/10 p-2 rounded text-xs overflow-x-auto my-2 [&>code]:bg-transparent [&>code]:p-0 [&>code]:text-[1em]"
        {...props}
      >
        {children}
      </pre>
    );
  },
  code: ({ node, className, children, ...props }) => {
    // Detect a ```chart``` fence emitted by tools and render an inline Recharts chart.
    if (typeof className === "string" && className.includes("language-chart")) {
      const raw = String(children || "").trim();
      try {
        const payload = JSON.parse(raw);
        return <ChatChart payload={payload} />;
      } catch (e) {
        // Malformed JSON — fall back to showing the raw block so nothing is silently lost.
        logger.warn("[chatbot] failed to parse chart payload, showing raw block:", e);
      }
    }
    // Siempre en línea. Esto miraba una prop `inline` que react-markdown dejó de
    // pasar en la v9 (el paquete instalado, 10.1.0, no la menciona ni una vez):
    // quedaba siempre `undefined`, así que TODO el código en línea caía en la rama
    // de bloque y un `.xlsx` partía la frase en tres. No dio error ni aviso porque
    // una prop que ya no existe se lee como falsa —el mismo molde que el resto de
    // los defectos de esta base: ante un dato ausente, contesta algo verosímil.
    return <code className="bg-black/5 dark:bg-white/10 px-1 py-0.5 rounded text-[0.85em]" {...props}>{children}</code>;
  },
  table: ({ node, ...props }) => (
    <div className="overflow-x-auto my-2">
      <table className="min-w-full text-xs border-collapse" {...props} />
    </div>
  ),
  th: ({ node, ...props }) => <th className="border border-border px-2 py-1 bg-muted text-left font-medium" {...props} />,
  td: ({ node, ...props }) => <td className="border border-border px-2 py-1" {...props} />,
  a: ({ node, ...props }) => <ChatLink {...props} />,
  h1: ({ node, ...props }) => <h1 className="font-semibold text-base mb-1" {...props} />,
  h2: ({ node, ...props }) => <h2 className="font-semibold text-sm mb-1" {...props} />,
  h3: ({ node, ...props }) => <h3 className="font-semibold text-sm mb-1" {...props} />,
};

// ============== FLOATING-BUTTON POSITION HELPERS ==============
const BUTTON_POS_KEY = "chatbot_button_pos_v1";
const BUTTON_SIZE = 56; // matches w-14 h-14
const BUTTON_FADE_RADIUS = 200; // px from cursor → opaque inside, faded outside
const BUTTON_FADE_OPACITY = 0.3;

function getDefaultButtonPos() {
  if (typeof window === "undefined") return { x: 0, y: 0 };
  // Equivalent to the original "fixed bottom-20 right-6" anchor
  return {
    x: window.innerWidth - BUTTON_SIZE - 24,
    y: window.innerHeight - BUTTON_SIZE - 80,
  };
}

function clampButtonPos(pos) {
  if (typeof window === "undefined") return pos;
  const margin = 8;
  return {
    x: Math.max(margin, Math.min(window.innerWidth - BUTTON_SIZE - margin, pos.x)),
    y: Math.max(margin, Math.min(window.innerHeight - BUTTON_SIZE - margin, pos.y)),
  };
}

function loadButtonPos() {
  try {
    const raw = localStorage.getItem(BUTTON_POS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (typeof p?.x === "number" && typeof p?.y === "number") return clampButtonPos(p);
  } catch {}
  return null;
}

export default function ChatbotWidget() {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [activeTool, setActiveTool] = useState(null);
  const [followups, setFollowups] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [showWelcome, setShowWelcome] = useState(true);
  const [showWork, setShowWork] = useState(() => {
    try { return localStorage.getItem(SHOW_WORK_KEY) === "1"; } catch { return false; }
  });
  const [pendingToolCalls, setPendingToolCalls] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  // Presupuesto IA mensual del tenant: {state: ok|degraded|blocked, pct, ...}
  const [budget, setBudget] = useState(null);

  // Floating button position (drag + persist) and proximity fade
  const initialButtonPos = useMemo(() => loadButtonPos() || getDefaultButtonPos(), []);
  const buttonX = useMotionValue(initialButtonPos.x);
  const buttonY = useMotionValue(initialButtonPos.y);
  const buttonRef = useRef(null);
  const [buttonOpacity, setButtonOpacity] = useState(1);
  const dragMovedRef = useRef(false);

  const persistButtonPos = () => {
    const next = clampButtonPos({ x: buttonX.get(), y: buttonY.get() });
    buttonX.set(next.x);
    buttonY.set(next.y);
    try { localStorage.setItem(BUTTON_POS_KEY, JSON.stringify(next)); } catch {}
  };

  const resetButtonPos = () => {
    const def = getDefaultButtonPos();
    buttonX.set(def.x);
    buttonY.set(def.y);
    try { localStorage.removeItem(BUTTON_POS_KEY); } catch {}
  };

  // Al cambiar el tamaño de la ventana, el botón vuelve a la esquina inferior
  // derecha y se olvida la posición arrastrada: una coordenada guardada para
  // otro tamaño ya no apunta al mismo lugar relativo.
  useEffect(() => {
    const onResize = () => {
      const def = getDefaultButtonPos();
      buttonX.set(def.x);
      buttonY.set(def.y);
      try { localStorage.removeItem(BUTTON_POS_KEY); } catch {}
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [buttonX, buttonY]);

  // Proximity fade — only while button is visible (chat closed). On touch
  // devices mousemove never fires, so the button stays fully opaque, which
  // is the safer default.
  useEffect(() => {
    if (isOpen) { setButtonOpacity(1); return; }
    const onMove = (e) => {
      const el = buttonRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const d = Math.hypot(e.clientX - cx, e.clientY - cy);
      setButtonOpacity(d < BUTTON_FADE_RADIUS ? 1 : BUTTON_FADE_OPACITY);
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [isOpen]);

  const loadSessions = async () => {
    try {
      const res = await fetch(`${API_BASE}/chat/sessions`, authFetchInit());
      if (!res.ok) return;
      const data = await res.json();
      setSessions(Array.isArray(data?.sessions) ? data.sessions : []);
    } catch (err) {
      logger.warn("[chatbot] loadSessions failed:", err);
    }
  };

  // Estado del presupuesto IA (best-effort): al abrir el widget y tras el
  // fallback no-streaming; el camino SSE lo refresca con el evento "done".
  const loadBudget = async () => {
    try {
      const res = await fetch(`${API_BASE}/chat/budget`, authFetchInit());
      if (!res.ok) return;
      setBudget(await res.json());
    } catch (err) {
      logger.warn("[chatbot] loadBudget failed:", err);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    loadBudget();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadSession = async (sid) => {
    try {
      if (abortRef.current) abortRef.current.abort();
      const res = await fetch(
        `${API_BASE}/chat/history?session_id=${encodeURIComponent(sid)}&limit=200`,
        authFetchInit(),
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const msgs = (Array.isArray(data?.messages) ? data.messages : []).map(m => ({
        role: m.role,
        content: m.content,
      }));
      setMessages(msgs);
      setSessionId(sid);
      setShowWelcome(false);
      setStreamingText("");
      setActiveTool(null);
      setPendingToolCalls([]);
      setFollowups([]);
      setHistoryOpen(false);
    } catch (err) {
      logger.error("[chatbot] loadSession failed:", err);
    }
  };

  const deleteSession = async (sid) => {
    try {
      const res = await fetch(
        `${API_BASE}/chat/sessions/${encodeURIComponent(sid)}`,
        authFetchInit({ method: "DELETE" }),
      );
      if (!res.ok) return;
      setSessions(prev => prev.filter(s => s.session_id !== sid));
      if (sessionId === sid) {
        // The session the user was on just got deleted — drop into a fresh state
        setMessages([]);
        setSessionId(null);
        setShowWelcome(true);
        setFollowups([]);
      }
    } catch (err) {
      logger.warn("[chatbot] deleteSession failed:", err);
    }
  };

  const startNewConversation = () => {
    if (abortRef.current) abortRef.current.abort();
    setMessages([]);
    setSessionId(null);
    setShowWelcome(true);
    setStreamingText("");
    setActiveTool(null);
    setPendingToolCalls([]);
    setFollowups([]);
    setHistoryOpen(false);
  };

  const toggleHistory = () => {
    setHistoryOpen(prev => {
      const next = !prev;
      if (next) loadSessions();
      return next;
    });
  };

  // Refresh sessions whenever a new assistant turn lands (best-effort, non-blocking)
  useEffect(() => {
    if (!isOpen) return;
    const lastIsAssistant = messages.length > 0 && messages[messages.length - 1]?.role === "assistant";
    if (lastIsAssistant && !loading) loadSessions();
  }, [messages, loading, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleShowWork = () => {
    setShowWork(prev => {
      const next = !prev;
      try { localStorage.setItem(SHOW_WORK_KEY, next ? "1" : "0"); } catch {}
      return next;
    });
  };
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingText, activeTool, pendingToolCalls]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const sendMessage = async (rawMessage) => {
    const userMessage = (rawMessage ?? input).trim();
    if (!userMessage || loading || budget?.state === "blocked") return;

    setInput("");
    setShowWelcome(false);
    setFollowups([]);
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);
    setStreamingText("");
    setActiveTool(null);
    setPendingToolCalls([]);

    const controller = new AbortController();
    abortRef.current = controller;

    const body = JSON.stringify({
      message: userMessage,
      session_id: sessionId,
      language: i18n.language || "es",
    });
    const fetchInit = authFetchInit({
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      signal: controller.signal,
    });

    // Helper: non-streaming fallback to /chat (used when /chat/stream isn't reachable)
    const runFallback = async () => {
      const res = await fetch(`${API_BASE}/chat`, fetchInit);
      if (!res.ok) {
        // Try to parse a typed error payload like {detail: {code, message}}
        let errCode = null;
        let errText = "";
        try {
          const errBody = await res.json();
          if (errBody?.detail?.code) {
            errCode = errBody.detail.code;
            errText = errBody.detail.message || "";
          } else if (typeof errBody?.detail === "string") {
            errText = errBody.detail;
          }
        } catch {
          errText = await res.text().catch(() => "");
        }
        if (!errCode && res.status === 429) errCode = "rate_limit";
        const err = new Error(errText || `HTTP ${res.status}`);
        err.code = errCode;
        throw err;
      }
      const json = await res.json();
      if (!sessionId && json.session_id) setSessionId(json.session_id);
      const cleanText = sanitizeStreaming(json.response || "");
      const tcs = Array.isArray(json.tool_calls) ? json.tool_calls : [];
      setMessages(prev => [...prev, { role: "assistant", content: cleanText, toolCalls: tcs }]);
      setStreamingText("");
      setActiveTool(null);
      setFollowups(Array.isArray(json.followups) ? json.followups : []);
      loadBudget();
    };

    try {
      let response;
      try {
        response = await fetch(`${API_BASE}/chat/stream`, fetchInit);
      } catch (netErr) {
        // Network-level failure on streaming endpoint — try fallback
        logger.warn("[chatbot] /chat/stream network error, falling back:", netErr);
        await runFallback();
        return;
      }

      // If the streaming endpoint isn't deployed (404/405) or the server can't
      // negotiate streaming for any reason, transparently fall back to /chat.
      if (response.status === 404 || response.status === 405) {
        logger.warn(`[chatbot] /chat/stream returned ${response.status}, falling back to /chat`);
        await runFallback();
        return;
      }

      if (!response.ok || !response.body) {
        // Intentar extraer un error tipado {detail: {code, ...}} (429 de cuota/presupuesto)
        let errCode = null;
        let errText = "";
        try {
          const errBody = await response.json();
          if (errBody?.detail?.code) {
            errCode = errBody.detail.code;
            errText = errBody.detail.message || "";
          } else if (typeof errBody?.detail === "string") {
            errText = errBody.detail;
          }
        } catch {
          errText = await response.text().catch(() => "");
        }
        if (!errCode && response.status === 429) errCode = "rate_limit";
        const err = new Error(errText || `HTTP ${response.status}`);
        err.code = errCode;
        throw err;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let accumulated = "";
      let receivedFollowups = [];
      let receivedToolCalls = [];
      let receivedProposals = [];
      let errorMessage = null;
      let errorCode = null;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const events = buffer.split("\n\n");
        buffer = events.pop() ?? "";

        for (const evt of events) {
          const line = evt.split("\n").find(l => l.startsWith("data: "));
          if (!line) continue;
          let payload;
          try {
            payload = JSON.parse(line.slice(6));
          } catch {
            continue;
          }
          if (payload.type === "session" && payload.session_id) {
            if (!sessionId) setSessionId(payload.session_id);
          } else if (payload.type === "tool") {
            setActiveTool(payload.name);
          } else if (payload.type === "tool_done") {
            // Strip the "type" field — keep just the call summary
            // eslint-disable-next-line no-unused-vars
            const { type, ...summary } = payload;
            receivedToolCalls.push(summary);
            setPendingToolCalls(prev => [...prev, summary]);
          } else if (payload.type === "action_proposal") {
            // Acción de escritura propuesta por el asistente — requiere confirmación
            // eslint-disable-next-line no-unused-vars
            const { type, ...proposal } = payload;
            receivedProposals.push({ ...proposal, status: "pending" });
          } else if (payload.type === "text") {
            accumulated += payload.chunk;
            setStreamingText(accumulated);
            setActiveTool(null);
          } else if (payload.type === "done") {
            receivedFollowups = payload.followups || [];
            if (payload.budget) setBudget(prev => ({ ...(prev || {}), ...payload.budget }));
          } else if (payload.type === "error") {
            errorMessage = payload.message || "stream error";
            errorCode = payload.code || null;
          }
        }
      }

      if (errorMessage) {
        const err = new Error(errorMessage);
        err.code = errorCode;
        throw err;
      }

      const cleanText = sanitizeStreaming(accumulated);
      setMessages(prev => [...prev, {
        role: "assistant", content: cleanText, toolCalls: receivedToolCalls,
        proposals: receivedProposals.length ? receivedProposals : undefined,
      }]);
      setStreamingText("");
      setActiveTool(null);
      setPendingToolCalls([]);
      setFollowups(receivedFollowups);
    } catch (error) {
      if (error.name !== "AbortError") {
        logger.error("[chatbot] sendMessage failed:", error);
        let content;
        if (error?.code === "ai_budget_exceeded") {
          // Presupuesto IA del mes agotado — reflejarlo también en el banner/input.
          content = t("chatbot.budget.blocked");
          setBudget(prev => ({ ...(prev || {}), state: "blocked", pct: 1 }));
        } else if (error?.code === "empty_response") {
          // El modelo se quedó sin texto incluso después del reintento sin
          // herramientas que hace el backend. El `message` que viene es una nota
          // técnica en español para el log; al usuario le sirve saber qué hacer,
          // y en su idioma.
          content = t("chatbot.errorEmptyResponse");
        } else if (error?.code === "rate_limit" || error?.code === "quota_exceeded") {
          // Friendly, no technical details — the user can't act on the underlying 429.
          content = t("chatbot.errorRateLimit");
        } else {
          const detail = error?.message ? ` (${error.message})` : "";
          content = `${t("chatbot.error")}${detail}`;
        }
        setMessages(prev => [...prev, {
          role: "assistant",
          content,
          isError: true,
        }]);
      }
      setStreamingText("");
      setActiveTool(null);
      setPendingToolCalls([]);
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  };

  // Confirmar/cancelar una propuesta de acción del asistente
  const handleProposalAction = async (msgIdx, proposalId, action) => {
    const setStatus = (status, resultMsg) => setMessages(prev => prev.map((m, i) => (
      i !== msgIdx ? m : {
        ...m,
        proposals: (m.proposals || []).map(p =>
          p.proposal_id === proposalId ? { ...p, status, resultMsg } : p
        ),
      }
    )));
    setStatus(action === "confirm" ? "confirming" : "rejecting");
    try {
      const res = await fetch(
        `${API_BASE}/chat/actions/${encodeURIComponent(proposalId)}/${action}`,
        authFetchInit({ method: "POST" }),
      );
      if (!res.ok) {
        let cuerpo = null;
        try { cuerpo = await res.json(); } catch {}
        const code = cuerpo?.detail?.code;
        if (res.status === 410 || code === "proposal_expired") { setStatus("expired"); return; }
        if (res.status === 409) { setStatus("expired"); return; } // ya no está pendiente
        // Una guarda de clasificar (la categoría ganó subcategorías entre la tarjeta y el
        // Confirmar): se dice cuál, no un error genérico.
        const guarda = textoDeErrorDelChat(res.status, cuerpo, t);
        if (guarda) { setStatus("failed", guarda); toast.error(guarda); return; }
        throw new Error(`HTTP ${res.status}`);
      }
      if (action === "confirm") {
        const json = await res.json().catch(() => ({}));
        // Clasificar desde el chat dice cuántas escribió y por qué omitió las demás (divididas,
        // período bloqueado, cambiaron mientras tanto, ya estaban, pendientes o devueltas a
        // Odoo) con las claves de la pantalla, en el idioma de quien lo usa. Sin los contadores
        // (un backend anterior, u otra acción), el `message` del servidor.
        setStatus("executed", textoDeTarjetaDelChat(json?.result, t));
        toast.success(t("chatbot.actions.confirmed"));
      } else {
        setStatus("rejected");
      }
    } catch (err) {
      logger.error("[chatbot] proposal action failed:", err);
      setStatus("failed");
      toast.error(t("chatbot.actions.error"));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    if (abortRef.current) abortRef.current.abort();
    setMessages([]);
    setSessionId(null);
    setShowWelcome(true);
    setStreamingText("");
    setActiveTool(null);
    setPendingToolCalls([]);
    setFollowups([]);
  };

  const quickQuestions = t("chatbot.quickQuestions", { returnObjects: true });
  const quickQuestionsArr = Array.isArray(quickQuestions) ? quickQuestions : [];

  return (
    <>
      {/* Floating Button — draggable, position persisted, fades when cursor is far */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            ref={buttonRef}
            drag
            dragMomentum={false}
            dragElastic={0}
            onDragStart={() => { dragMovedRef.current = true; }}
            onDragEnd={() => { persistButtonPos(); }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: buttonOpacity }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ opacity: { duration: 0.2 } }}
            style={{ x: buttonX, y: buttonY, top: 0, left: 0, touchAction: "none" }}
            onClick={() => {
              if (dragMovedRef.current) {
                dragMovedRef.current = false;
                return;
              }
              setIsOpen(true);
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              resetButtonPos();
              toast.success(t("chatbot.resetPosition"));
            }}
            data-testid="chatbot-open-btn"
            title={`${t("chatbot.openButton")} — ${t("chatbot.dragHint")}`}
            className="fixed z-50 w-14 h-14 rounded-full shadow-lg bg-primary hover:bg-primary/90 flex items-center justify-center text-primary-foreground cursor-grab active:cursor-grabbing select-none"
          >
            <MessageCircle className="w-6 h-6 pointer-events-none" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className={cn(
              "fixed z-50 shadow-2xl rounded-lg overflow-hidden",
              isMinimized
                ? "bottom-20 right-6 w-72"
                : isMaximized
                  ? "inset-4 sm:inset-8 lg:inset-12 max-w-[1100px] max-h-[900px] mx-auto"
                  : "bottom-20 right-6 w-96 h-[560px]"
            )}
          >
            <Card className="h-full flex flex-col border-primary/20">
              {/* Header */}
              <CardHeader className="py-3 px-4 bg-primary text-primary-foreground flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Bot className="w-5 h-5" />
                    <CardTitle className="text-base font-medium">{t("chatbot.title")}</CardTitle>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn(
                        "h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20",
                        historyOpen && "bg-primary-foreground/20"
                      )}
                      onClick={toggleHistory}
                      title={t("chatbot.history.toggle")}
                    >
                      <History className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn(
                        "h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20",
                        showWork && "bg-primary-foreground/20"
                      )}
                      onClick={toggleShowWork}
                      title={showWork ? t("chatbot.showWorkOn") : t("chatbot.showWorkOff")}
                    >
                      <Wrench className="w-4 h-4" />
                    </Button>
                    {messages.length > 0 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
                        onClick={clearChat}
                        title={t("chatbot.newConversation")}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                    {!isMinimized && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
                        onClick={() => setIsMaximized(!isMaximized)}
                        title={isMaximized ? t("chatbot.collapse", "Reducir") : t("chatbot.expand", "Expandir")}
                      >
                        {isMaximized ? <Shrink className="w-4 h-4" /> : <Expand className="w-4 h-4" />}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
                      onClick={() => {
                        setIsMinimized(!isMinimized);
                        if (!isMinimized) setIsMaximized(false);
                      }}
                      title={isMinimized ? t("chatbot.restore", "Restaurar") : t("chatbot.minimize", "Minimizar")}
                    >
                      {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
                      onClick={() => setIsOpen(false)}
                      data-testid="chatbot-close-btn"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>

              {/* Body - Hidden when minimized */}
              {!isMinimized && (
                <>
                  {historyOpen ? (
                    <CardContent className="flex-1 p-0 overflow-hidden min-h-0">
                      <HistoryPanel
                        sessions={sessions}
                        currentSessionId={sessionId}
                        onSelect={loadSession}
                        onDelete={deleteSession}
                        onNew={startNewConversation}
                        onClose={() => setHistoryOpen(false)}
                        t={t}
                      />
                    </CardContent>
                  ) : (
                  <>
                  <CardContent className="flex-1 p-0 overflow-hidden min-h-0">
                    <ScrollArea className="h-full p-4">
                      {/* Welcome Message */}
                      {showWelcome && messages.length === 0 && (
                        <div className="space-y-4">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <Bot className="w-4 h-4 text-primary" />
                            </div>
                            <div className="bg-muted rounded-lg p-3 max-w-[85%]">
                              <p className="text-sm font-medium mb-1">{t("chatbot.welcomeTitle")}</p>
                              <p className="text-sm text-muted-foreground">{t("chatbot.welcomeBody")}</p>
                            </div>
                          </div>

                          {/* Quick Questions */}
                          {quickQuestionsArr.length > 0 && (
                            <div className="pl-11 space-y-2">
                              <p className="text-xs text-muted-foreground">{t("chatbot.quickQuestionsLabel")}</p>
                              <div className="flex flex-wrap gap-2">
                                {quickQuestionsArr.map((q, i) => (
                                  <Badge
                                    key={i}
                                    variant="outline"
                                    className="cursor-pointer hover:bg-primary/10 transition-colors"
                                    onClick={() => sendMessage(q)}
                                  >
                                    {q}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Messages */}
                      {messages.map((msg, idx) => (
                        <div
                          key={idx}
                          className={cn(
                            "flex items-start gap-3 mb-4",
                            msg.role === "user" && "flex-row-reverse"
                          )}
                        >
                          <div className={cn(
                            "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                            msg.role === "user" ? "bg-primary" : "bg-primary/10"
                          )}>
                            {msg.role === "user" ? (
                              <User className="w-4 h-4 text-primary-foreground" />
                            ) : (
                              <Bot className="w-4 h-4 text-primary" />
                            )}
                          </div>
                          <div className={cn(
                            "rounded-lg p-3 max-w-[85%] text-sm break-words",
                            msg.role === "user"
                              ? "bg-primary text-primary-foreground whitespace-pre-wrap"
                              : msg.isError
                                ? "bg-destructive/10 text-destructive whitespace-pre-wrap"
                                : "bg-muted"
                          )}>
                            {msg.role === "assistant" && !msg.isError ? (
                              <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
                                {msg.content}
                              </ReactMarkdown>
                            ) : (
                              <p>{msg.content}</p>
                            )}
                            {msg.role === "assistant" && !msg.isError && Array.isArray(msg.proposals) &&
                              msg.proposals.map((p) => (
                                <ActionProposalCard
                                  key={p.proposal_id}
                                  proposal={p}
                                  t={t}
                                  onConfirm={() => handleProposalAction(idx, p.proposal_id, "confirm")}
                                  onReject={() => handleProposalAction(idx, p.proposal_id, "reject")}
                                />
                              ))}
                            {showWork && msg.role === "assistant" && !msg.isError && (
                              <ToolCallsDetails toolCalls={msg.toolCalls} t={t} />
                            )}
                          </div>
                        </div>
                      ))}

                      {/* Live streaming bubble */}
                      {(loading || streamingText) && (
                        <div className="flex items-start gap-3 mb-4">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Bot className="w-4 h-4 text-primary" />
                          </div>
                          <div className="bg-muted rounded-lg p-3 max-w-[85%] text-sm break-words">
                            {activeTool && !streamingText ? (
                              <p className="text-xs text-muted-foreground italic">
                                {t("chatbot.usingTool", { tool: activeTool })}
                              </p>
                            ) : streamingText ? (
                              <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
                                {sanitizeStreaming(streamingText)}
                              </ReactMarkdown>
                            ) : (
                              <TypingDots />
                            )}
                            {showWork && pendingToolCalls.length > 0 && (
                              <ToolCallsDetails toolCalls={pendingToolCalls} t={t} />
                            )}
                          </div>
                        </div>
                      )}

                      {/* Follow-up suggestions */}
                      {!loading && followups.length > 0 && (
                        <motion.div
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.25 }}
                          className="pl-11 mb-4"
                        >
                          <div className="flex items-center gap-1.5 mb-2 text-primary/70">
                            <Sparkles className="w-3 h-3" />
                            <p className="text-xs font-medium">{t("chatbot.followupsLabel")}</p>
                          </div>
                          <div className="flex flex-col gap-1.5">
                            {followups.map((q, i) => (
                              <button
                                key={i}
                                onClick={() => sendMessage(q)}
                                className={cn(
                                  "group flex items-center justify-between gap-2 text-left",
                                  "px-3 py-2 rounded-lg border border-primary/20 bg-primary/5",
                                  "hover:bg-primary/10 hover:border-primary/40 transition-all",
                                  "text-sm text-foreground"
                                )}
                              >
                                <span className="flex-1 leading-snug">{q}</span>
                                <ArrowRight className="w-3.5 h-3.5 text-primary/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0" />
                              </button>
                            ))}
                          </div>
                        </motion.div>
                      )}

                      <div ref={messagesEndRef} />
                    </ScrollArea>
                  </CardContent>

                  {/* Input */}
                  <div className="p-3 border-t bg-background flex-shrink-0">
                    {/* Contador de uso del presupuesto IA: siempre en %, nunca en monto */}
                    {typeof budget?.pct === "number" && (
                      <div
                        className="flex items-center gap-2 mb-2 px-0.5"
                        data-testid="chatbot-budget-usage"
                      >
                        <div className="flex-1 h-1 rounded-full bg-muted overflow-hidden">
                          <div
                            className={cn(
                              "h-full rounded-full transition-all",
                              budget.pct >= 1 ? "bg-destructive"
                                : budget.pct >= 0.8 ? "bg-yellow-500"
                                : "bg-primary"
                            )}
                            style={{ width: `${Math.min(Math.round(budget.pct * 100), 100)}%` }}
                          />
                        </div>
                        <p className="text-[11px] leading-none text-muted-foreground whitespace-nowrap">
                          {t("chatbot.budget.usage", { pct: Math.min(Math.round(budget.pct * 100), 100) })}
                        </p>
                      </div>
                    )}
                    {/* Presupuesto IA: aviso de modo económico (>=80%) o bloqueo (100%) */}
                    {budget?.state === "degraded" && (
                      <div className="flex items-center gap-2 mb-2 px-2.5 py-1.5 rounded-md bg-yellow-500/10 border border-yellow-500/30 text-yellow-700 dark:text-yellow-400">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                        <p className="text-xs leading-snug">
                          {t("chatbot.budget.warning", { pct: Math.round((budget.pct || 0) * 100) })}
                        </p>
                      </div>
                    )}
                    {budget?.state === "blocked" && (
                      <div className="flex items-center gap-2 mb-2 px-2.5 py-1.5 rounded-md bg-destructive/10 border border-destructive/30 text-destructive">
                        <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                        <p className="text-xs leading-snug flex-1">{t("chatbot.budget.blocked")}</p>
                        <RouterLink
                          to="/subscription"
                          className="text-xs font-medium underline whitespace-nowrap hover:opacity-80"
                          onClick={() => setIsOpen(false)}
                        >
                          {t("chatbot.budget.upgradeCta")}
                        </RouterLink>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Input
                        ref={inputRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder={t("chatbot.inputPlaceholder")}
                        disabled={loading || budget?.state === "blocked"}
                        className="flex-1"
                        data-testid="chatbot-input"
                      />
                      <Button
                        onClick={() => sendMessage()}
                        disabled={!input.trim() || loading || budget?.state === "blocked"}
                        size="icon"
                        data-testid="chatbot-send-btn"
                      >
                        {loading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  </>
                  )}
                </>
              )}
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
